import { ENTITY_CLASS, ResidentQuery, type UserKey } from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

import { fetchData, findResidents, generateResidentImageGetUrl } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

// =============================================================
// 入居者の単体取得
// =============================================================

export async function findResident(userKey: UserKey | false | null | undefined) {
  if (!userKey) {
    return undefined;
  }
  const { output } = await fetchData({ type: "Entity::Resident", input: { userKey } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function ResidentQueryKey(userKey: UserKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.RESIDENT, userKey);
}

export function updateResidentQueryData(
  userKey: UserKey | false | null | undefined,
  newResident: ReturnType<typeof useResidentQuery>["data"] | false,
) {
  if (userKey && newResident !== false && newResident !== undefined) {
    const queryKey = ResidentQueryKey(userKey);
    TANSTACK_QUERY_CLIENT.setQueryData(
      queryKey,
      function (oldResident: ReturnType<typeof useResidentQuery>["data"]) {
        return !oldResident ||
          !newResident ||
          (Temporal.PlainDateTime.compare(oldResident.createdAt, newResident.createdAt) ||
            Temporal.PlainDateTime.compare(oldResident.updatedAt, newResident.updatedAt)) < 0
          ? newResident
          : oldResident;
      },
    );
  }
}

/** @deprecated 代わりに`useResidentQuery`を使用する。 */
export function useResident(
  userKey: UserKey | false | null | undefined,
  options?: {
    /** 仮想化された一覧で表示される場合に`true`を設定するフラグ */
    virtualized?: boolean;
  },
) {
  const { data: resident } = useQuery({
    queryKey: ResidentQueryKey(userKey),
    async queryFn() {
      return findResident(userKey);
    },

    enabled: !!userKey,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: !options?.virtualized,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  return resident;
}

export function useResidentQuery(userKey: UserKey | false | null | undefined) {
  return useQuery({
    queryKey: ResidentQueryKey(userKey),
    async queryFn() {
      return findResident(userKey);
    },

    enabled: Boolean(userKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

function ResidentImageQueryKey(userKey: UserKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.RESIDENT + ".Image", userKey);
}

export function invalidateResidentImageQuery(userKey: UserKey | false | null | undefined) {
  TANSTACK_QUERY_CLIENT.invalidateQueries({
    exact: true,
    queryKey: ResidentImageQueryKey(userKey),
  });
}

export function useResidentImageQuery(userKey: UserKey | false | null | undefined) {
  return useQuery({
    queryKey: ResidentImageQueryKey(userKey),
    async queryFn() {
      if (!userKey) {
        return undefined;
      }

      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        return generateResidentImageGetUrl({ userKey });
      } finally {
        release();
      }
    },

    enabled: Boolean(userKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 7日間 */ 604_800_000,
  });
}

// =============================================================
// 入居者の一覧
// =============================================================

function ResidentInfiniteQueryKey(...parameters: Parameters<typeof useResidentInfiniteQuery>) {
  return QueryKey(ENTITY_CLASS.RESIDENT + "List", ...parameters);
}

export function removeResidentInfiniteQueryCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({ queryKey: QueryKey(ENTITY_CLASS.RESIDENT + "List") });
}

export function useResidentInfiniteQuery(
  query: ResidentQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: ResidentInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { residents, errors, nextPageToken } = await findResidents({
          ...query,
          ...context.pageParam,
        });

        for (const resident of residents) {
          updateResidentQueryData(resident.userKey, resident);
        }

        return { residents, errors, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}
